import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import Breadcrumb from '../../components/ui/Breadcrumb';

const emptyCategory = { name: '', name_en: '', sort_order: 0 };
const emptyType = { budget_document_category_id: '', name: '', name_en: '', series: '', folio_sequence: 1, footer_text: '', footer_text_en: '', show_bank_accounts: true, sort_order: 0 };
const emptyConcCategory = { name: '', name_en: '', budget_bank_account_id: '' };
const emptyConcept = { name: '', name_en: '', default_amount: 0, includes_iva: false, iva_rate: 16, type: 'normal' };
const emptyAccount = { label: '', label_en: '', bank: '', beneficiary: '', account_number: '', clabe: '', swift: '', branch: '', logo: null };

const BudgetDocumentTypes = () => {
  const [docCategories, setDocCategories] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);

  const [showDocCatForm, setShowDocCatForm] = useState(false);
  const [editingDocCat, setEditingDocCat] = useState(null);
  const [docCatForm, setDocCatForm] = useState(emptyCategory);

  const [showTypeForm, setShowTypeForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeForm, setTypeForm] = useState(emptyType);

  const [showConCatForm, setShowConCatForm] = useState(false);
  const [editingConCat, setEditingConCat] = useState(null);
  const [conCatForm, setConCatForm] = useState(emptyConcCategory);

  const [showConceptForm, setShowConceptForm] = useState(null);
  const [editingConcept, setEditingConcept] = useState(null);
  const [conceptForm, setConceptForm] = useState(emptyConcept);

  const [showAccountForm, setShowAccountForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accountForm, setAccountForm] = useState(emptyAccount);

  const [submitting, setSubmitting] = useState(false);

  const fetchAll = async () => {
    try {
      const [catsRes, typesRes] = await Promise.all([
        api.get('/budget-document-categories'),
        api.get('/budget-document-types'),
      ]);
      setDocCategories(catsRes.data);
      setDocTypes(typesRes.data);
      if (selectedType) {
        const updated = typesRes.data.find(t => t.id === selectedType.id);
        setSelectedType(updated || null);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // ── Categorías de documento ──
  const handleSaveDocCat = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingDocCat) {
        await api.put(`/budget-document-categories/${editingDocCat.id}`, docCatForm);
        toast.success('Categoría actualizada');
      } else {
        await api.post('/budget-document-categories', docCatForm);
        toast.success('Categoría creada');
      }
      setShowDocCatForm(false); setEditingDocCat(null); setDocCatForm(emptyCategory);
      fetchAll();
    } catch { toast.error('Error al guardar'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteDocCat = async (cat) => {
    if (!window.confirm(`¿Eliminar categoría "${cat.name}"?`)) return;
    try { await api.delete(`/budget-document-categories/${cat.id}`); toast.success('Categoría eliminada'); fetchAll(); }
    catch { toast.error('Error al eliminar'); }
  };

  // ── Tipos de documento ──
  const handleSaveType = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingType) {
        await api.put(`/budget-document-types/${editingType.id}`, typeForm);
        toast.success('Tipo actualizado');
      } else {
        await api.post('/budget-document-types', typeForm);
        toast.success('Tipo creado');
      }
      setShowTypeForm(false); setEditingType(null); setTypeForm(emptyType);
      fetchAll();
    } catch { toast.error('Error al guardar'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteType = async (type) => {
    if (!window.confirm(`¿Eliminar tipo "${type.name}"?`)) return;
    try {
      await api.delete(`/budget-document-types/${type.id}`);
      toast.success('Tipo eliminado');
      if (selectedType?.id === type.id) setSelectedType(null);
      fetchAll();
    } catch { toast.error('Error al eliminar'); }
  };

  // ── Categorías de conceptos ──
  const handleSaveConCat = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingConCat) {
        await api.put(`/budget-document-types/${selectedType.id}/concept-categories/${editingConCat.id}`, conCatForm);
        toast.success('Categoría actualizada');
      } else {
        await api.post(`/budget-document-types/${selectedType.id}/concept-categories`, conCatForm);
        toast.success('Categoría creada');
      }
      setShowConCatForm(false); setEditingConCat(null); setConCatForm(emptyConcCategory);
      fetchAll();
    } catch { toast.error('Error al guardar'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteConCat = async (cat) => {
    if (!window.confirm(`¿Eliminar categoría "${cat.name}" y todos sus conceptos?`)) return;
    try { await api.delete(`/budget-document-types/${selectedType.id}/concept-categories/${cat.id}`); toast.success('Categoría eliminada'); fetchAll(); }
    catch { toast.error('Error al eliminar'); }
  };

  // ── Conceptos ──
  const handleSaveConcept = async (e, categoryId) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingConcept) {
        await api.put(`/concept-categories/${categoryId}/concepts/${editingConcept.id}`, conceptForm);
        toast.success('Concepto actualizado');
      } else {
        await api.post(`/concept-categories/${categoryId}/concepts`, conceptForm);
        toast.success('Concepto creado');
      }
      setShowConceptForm(null); setEditingConcept(null); setConceptForm(emptyConcept);
      fetchAll();
    } catch { toast.error('Error al guardar'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteConcept = async (categoryId, concept) => {
    if (!window.confirm(`¿Eliminar concepto "${concept.name}"?`)) return;
    try { await api.delete(`/concept-categories/${categoryId}/concepts/${concept.id}`); toast.success('Concepto eliminado'); fetchAll(); }
    catch { toast.error('Error al eliminar'); }
  };

  // ── Cuentas bancarias ──
  const handleSaveAccount = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(accountForm).forEach(([key, value]) => {
        if (key === 'logo') {
          if (value) formData.append('logo', value);
        } else {
          formData.append(key, value ?? '');
        }
      });

      if (editingAccount) {
        await api.post(
          `/budget-document-types/${selectedType.id}/bank-accounts/${editingAccount.id}/update`,
          formData
        );
        toast.success('Cuenta actualizada');
      } else {
        await api.post(
          `/budget-document-types/${selectedType.id}/bank-accounts`,
          formData
        );
        toast.success('Cuenta creada');
      }
      setShowAccountForm(false);
      setEditingAccount(null);
      setAccountForm(emptyAccount);
      fetchAll();
    } catch {
      toast.error('Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAccount = async (account) => {
    if (!window.confirm(`¿Eliminar cuenta "${account.label}"?`)) return;
    try { await api.delete(`/budget-document-types/${selectedType.id}/bank-accounts/${account.id}`); toast.success('Cuenta eliminada'); fetchAll(); }
    catch { toast.error('Error al eliminar'); }
  };

  const typeLabels = { normal: 'Normal', informative: 'Informativo', discount: 'Descuento' };
  const typeColors = { normal: 'badge-primary', informative: 'badge-secondary', discount: 'badge-danger' };

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>;

  return (
    <div className="container-fluid">
      <Breadcrumb items={[{ label: 'Configuración' }, { label: 'Tipos de Documento' }]} />
      <div className="page-header d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Tipos de Documento</h4>
      </div>

      <div className="row">
        {/* Panel izquierdo */}
        <div className="col-12 col-md-4">
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Categorías</h6>
              <button className="btn btn-primary btn-sm" onClick={() => { setShowDocCatForm(true); setEditingDocCat(null); setDocCatForm(emptyCategory); }}>
                <i className="ti-plus"></i>
              </button>
            </div>
            <div className="card-body p-0">
              {showDocCatForm && (
                <div className="p-3 border-bottom bg-light">
                  <form onSubmit={handleSaveDocCat}>
                    <div className="form-group mb-2">
                      <label className="small mb-1">Nombre ES *</label>
                      <input type="text" className="form-control form-control-sm"
                        value={docCatForm.name} onChange={e => setDocCatForm({ ...docCatForm, name: e.target.value })} required />
                    </div>
                    <div className="form-group mb-2">
                      <label className="small mb-1">Nombre EN</label>
                      <input type="text" className="form-control form-control-sm"
                        value={docCatForm.name_en} onChange={e => setDocCatForm({ ...docCatForm, name_en: e.target.value })} />
                    </div>
                    <div className="d-flex" style={{ gap: '6px' }}>
                      <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>Guardar</button>
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setShowDocCatForm(false); setEditingDocCat(null); }}>Cancelar</button>
                    </div>
                  </form>
                </div>
              )}
              {docCategories.length === 0 && !showDocCatForm && (
                <div className="text-center text-muted py-3 small">Sin categorías</div>
              )}
              {docCategories.map(cat => (
                <div key={cat.id}>
                  <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom" style={{ backgroundColor: '#f8f9fa' }}>
                    <strong className="small">{cat.name}</strong>
                    <div className="d-flex" style={{ gap: '4px' }}>
                      <button className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 6px' }}
                        onClick={() => { setEditingDocCat(cat); setDocCatForm({ name: cat.name, name_en: cat.name_en || '' }); setShowDocCatForm(true); }}>
                        <i className="ti-pencil"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-danger" style={{ padding: '2px 6px' }} onClick={() => handleDeleteDocCat(cat)}>
                        <i className="ti-trash"></i>
                      </button>
                    </div>
                  </div>
                  {docTypes.filter(t => t.budget_document_category_id === cat.id).map(type => (
                    <div key={type.id}
                      className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom"
                      style={{ cursor: 'pointer', backgroundColor: selectedType?.id === type.id ? '#EEF0FB' : '#fff' }}
                      onClick={() => setSelectedType(type)}>
                      <div>
                        <div className="small font-weight-bold" style={{ color: selectedType?.id === type.id ? '#5066E1' : 'inherit' }}>
                          {type.series && <span className="badge badge-secondary mr-1" style={{ fontSize: '10px' }}>{type.series}</span>}
                          {type.name}
                        </div>
                      </div>
                      <div className="d-flex" style={{ gap: '4px' }} onClick={e => e.stopPropagation()}>
                        <button className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 6px' }}
                          onClick={() => {
                            setEditingType(type);
                            setTypeForm({
                              budget_document_category_id: type.budget_document_category_id,
                              name: type.name, name_en: type.name_en || '',
                              series: type.series || '', folio_sequence: type.folio_sequence,
                              footer_text: type.footer_text || '', footer_text_en: type.footer_text_en || '',
                              show_bank_accounts: type.show_bank_accounts,
                            });
                            setShowTypeForm(true);
                          }}>
                          <i className="ti-pencil"></i>
                        </button>
                        <button className="btn btn-sm btn-outline-danger" style={{ padding: '2px 6px' }} onClick={() => handleDeleteType(type)}>
                          <i className="ti-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className="card-footer">
              <button className="btn btn-outline-primary btn-sm btn-block" onClick={() => { setShowTypeForm(true); setEditingType(null); setTypeForm(emptyType); }}>
                <i className="ti-plus mr-1"></i> Nuevo Tipo de Documento
              </button>
            </div>
          </div>

          {showTypeForm && (
            <div className="card mb-3">
              <div className="card-header"><h6 className="mb-0">{editingType ? 'Editar Tipo' : 'Nuevo Tipo'}</h6></div>
              <div className="card-body">
                <form onSubmit={handleSaveType}>
                  <div className="form-group">
                    <label className="small">Categoría *</label>
                    <select className="form-control form-control-sm" value={typeForm.budget_document_category_id}
                      onChange={e => setTypeForm({ ...typeForm, budget_document_category_id: e.target.value })} required>
                      <option value="">Seleccionar...</option>
                      {docCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="small">Nombre ES *</label>
                    <input type="text" className="form-control form-control-sm" value={typeForm.name}
                      onChange={e => setTypeForm({ ...typeForm, name: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="small">Nombre EN</label>
                    <input type="text" className="form-control form-control-sm" value={typeForm.name_en}
                      onChange={e => setTypeForm({ ...typeForm, name_en: e.target.value })} />
                  </div>
                  <div className="row">
                    <div className="col-6">
                      <div className="form-group">
                        <label className="small">Serie</label>
                        <input type="text" className="form-control form-control-sm" placeholder="com"
                          value={typeForm.series} onChange={e => setTypeForm({ ...typeForm, series: e.target.value })} />
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label className="small">Folio inicial</label>
                        <input type="number" className="form-control form-control-sm" min="1"
                          value={typeForm.folio_sequence} onChange={e => setTypeForm({ ...typeForm, folio_sequence: e.target.value })} />
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="small">Pie de página ES</label>
                    <textarea className="form-control form-control-sm" rows="2" value={typeForm.footer_text}
                      onChange={e => setTypeForm({ ...typeForm, footer_text: e.target.value })}></textarea>
                  </div>
                  <div className="form-group">
                    <label className="small">Pie de página EN</label>
                    <textarea className="form-control form-control-sm" rows="2" value={typeForm.footer_text_en}
                      onChange={e => setTypeForm({ ...typeForm, footer_text_en: e.target.value })}></textarea>
                  </div>
                  <div className="custom-control custom-checkbox mb-3">
                    <input type="checkbox" className="custom-control-input" id="showBankAccounts"
                      checked={typeForm.show_bank_accounts}
                      onChange={e => setTypeForm({ ...typeForm, show_bank_accounts: e.target.checked })} />
                    <label className="custom-control-label small" htmlFor="showBankAccounts">
                      Mostrar cuentas bancarias en PDF
                    </label>
                  </div>
                  <div className="d-flex flex-wrap" style={{ gap: '6px' }}>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>Guardar</button>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setShowTypeForm(false); setEditingType(null); }}>Cancelar</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>

        {/* Panel derecho */}
        <div className="col-12 col-md-8">
          {!selectedType ? (
            <div className="card">
              <div className="card-body text-center text-muted py-5">
                <svg style={{ width: 48, height: 48, margin: '0 auto 12px', display: 'block', color: '#d1d5db' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Selecciona un tipo de documento para configurar sus conceptos y cuentas bancarias
              </div>
            </div>
          ) : (
            <>
              {/* Categorías de conceptos */}
              <div className="card mb-3">
                <div className="card-header">
                  <h6 className="mb-0">{selectedType.name} <span className="text-muted font-weight-normal small">— Categorías de Conceptos</span></h6>
                </div>
                <div className="card-body">
                  {showConCatForm && (
                    <div className="border rounded p-3 mb-3 bg-light">
                      <form onSubmit={handleSaveConCat}>
                        <div className="row">
                          <div className="col-12 col-md-6">
                            <div className="form-group mb-2">
                              <label className="small">Nombre ES *</label>
                              <input type="text" className="form-control form-control-sm"
                                placeholder="Impuestos, derechos y gastos a cubrir"
                                value={conCatForm.name} onChange={e => setConCatForm({ ...conCatForm, name: e.target.value })} required />
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form-group mb-2">
                              <label className="small">Nombre EN</label>
                              <input type="text" className="form-control form-control-sm"
                                placeholder="Taxes, fees and expenses"
                                value={conCatForm.name_en} onChange={e => setConCatForm({ ...conCatForm, name_en: e.target.value })} />
                            </div>
                          </div>
                          {/* Cuenta bancaria asociada */}
                          <div className="col-12">
                            <div className="form-group mb-2">
                              <label className="small font-weight-bold">
                                Cuenta bancaria asociada
                                <span className="text-muted ml-1 font-weight-normal">— el subtotal de esta categoría se mostrará en esa cuenta</span>
                              </label>
                              <select className="form-control form-control-sm"
                                value={conCatForm.budget_bank_account_id || ''}
                                onChange={e => setConCatForm({ ...conCatForm, budget_bank_account_id: e.target.value })}>
                                <option value="">Sin cuenta asociada</option>
                                {selectedType?.bank_accounts?.map(acc => (
                                  <option key={acc.id} value={acc.id}>{acc.label}</option>
                                ))}
                              </select>
                              {(!selectedType?.bank_accounts || selectedType.bank_accounts.length === 0) && (
                                <small className="text-warning d-block mt-1">⚠ Primero agrega cuentas bancarias a este tipo de documento</small>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="d-flex" style={{ gap: '6px' }}>
                          <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>Guardar</button>
                          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setShowConCatForm(false); setEditingConCat(null); }}>Cancelar</button>
                        </div>
                      </form>
                    </div>
                  )}

                  {selectedType.concept_categories?.length === 0 && !showConCatForm && (
                    <div className="text-center text-muted py-3 small">Sin categorías de conceptos</div>
                  )}

                  {selectedType.concept_categories?.map(cat => (
                    <div key={cat.id} className="border rounded mb-3">
                      <div className="d-flex justify-content-between align-items-center p-2 border-bottom" style={{ backgroundColor: '#f8f9fa' }}>
                        <div>
                          <strong className="small">{cat.name}</strong>
                          {cat.name_en && <span className="text-muted small ml-2">/ {cat.name_en}</span>}
                          {/* Mostrar cuenta asociada */}
                          {cat.budget_bank_account_id && (
                            <span className="badge badge-info ml-2" style={{ fontSize: '10px' }}>
                              {selectedType.bank_accounts?.find(a => a.id === cat.budget_bank_account_id)?.label || 'Cuenta asociada'}
                            </span>
                          )}
                        </div>
                        <div className="d-flex" style={{ gap: '4px' }}>
                          <button className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 6px' }}
                            onClick={() => {
                              setEditingConCat(cat);
                              setConCatForm({ name: cat.name, name_en: cat.name_en || '', budget_bank_account_id: cat.budget_bank_account_id || '' });
                              setShowConCatForm(true);
                            }}>
                            <i className="ti-pencil"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-primary" style={{ padding: '2px 6px' }}
                            onClick={() => { setShowConceptForm(cat.id); setEditingConcept(null); setConceptForm(emptyConcept); }}>
                            <i className="ti-plus"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-danger" style={{ padding: '2px 6px' }} onClick={() => handleDeleteConCat(cat)}>
                            <i className="ti-trash"></i>
                          </button>
                        </div>
                      </div>

                      {showConceptForm === cat.id && (
                        <div className="p-3 border-bottom bg-light">
                          <form onSubmit={e => handleSaveConcept(e, cat.id)}>
                            <div className="row">
                              <div className="col-12 col-md-6">
                                <div className="form-group mb-2">
                                  <label className="small">Nombre ES *</label>
                                  <input type="text" className="form-control form-control-sm"
                                    value={conceptForm.name} onChange={e => setConceptForm({ ...conceptForm, name: e.target.value })} required />
                                </div>
                              </div>
                              <div className="col-12 col-md-6">
                                <div className="form-group mb-2">
                                  <label className="small">Nombre EN</label>
                                  <input type="text" className="form-control form-control-sm"
                                    value={conceptForm.name_en} onChange={e => setConceptForm({ ...conceptForm, name_en: e.target.value })} />
                                </div>
                              </div>
                              <div className="col-6 col-md-4">
                                <div className="form-group mb-2">
                                  <label className="small">Monto base</label>
                                  <input type="number" className="form-control form-control-sm" min="0" step="0.01"
                                    value={conceptForm.default_amount} onChange={e => setConceptForm({ ...conceptForm, default_amount: e.target.value })} />
                                </div>
                              </div>
                              <div className="col-6 col-md-4">
                                <div className="form-group mb-2">
                                  <label className="small">Tipo</label>
                                  <select className="form-control form-control-sm" value={conceptForm.type}
                                    onChange={e => setConceptForm({ ...conceptForm, type: e.target.value })}>
                                    <option value="normal">Normal</option>
                                    <option value="informative">Informativo</option>
                                    <option value="discount">Descuento</option>
                                  </select>
                                </div>
                              </div>
                              <div className="col-6 col-md-4">
                                <div className="form-group mb-2">
                                  <label className="small">IVA %</label>
                                  <input type="number" className="form-control form-control-sm" min="0" max="100" step="0.01"
                                    value={conceptForm.iva_rate} onChange={e => setConceptForm({ ...conceptForm, iva_rate: e.target.value })}
                                    disabled={!conceptForm.includes_iva} />
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="custom-control custom-checkbox mb-2">
                                  <input type="checkbox" className="custom-control-input" id={`iva-${cat.id}`}
                                    checked={conceptForm.includes_iva}
                                    onChange={e => setConceptForm({ ...conceptForm, includes_iva: e.target.checked })} />
                                  <label className="custom-control-label small" htmlFor={`iva-${cat.id}`}>Incluye IVA</label>
                                </div>
                              </div>
                            </div>
                            <div className="d-flex" style={{ gap: '6px' }}>
                              <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>Guardar</button>
                              <button type="button" className="btn btn-outline-secondary btn-sm"
                                onClick={() => { setShowConceptForm(null); setEditingConcept(null); }}>Cancelar</button>
                            </div>
                          </form>
                        </div>
                      )}

                      {cat.concepts?.length === 0 && showConceptForm !== cat.id && (
                        <div className="text-center text-muted py-2 small">Sin conceptos</div>
                      )}
                      {cat.concepts?.map(concept => (
                        <div key={concept.id} className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
                          <div style={{ flex: 1 }}>
                            <span className={`badge ${typeColors[concept.type]} mr-2`} style={{ fontSize: '10px' }}>
                              {typeLabels[concept.type]}
                            </span>
                            <span className="small">{concept.name}</span>
                            {concept.name_en && <span className="text-muted small ml-1">/ {concept.name_en}</span>}
                            {concept.includes_iva && <span className="badge badge-warning ml-1" style={{ fontSize: '10px' }}>+IVA {concept.iva_rate}%</span>}
                          </div>
                          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                            <span className="small text-muted">${Number(concept.default_amount).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                            <button className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 6px' }}
                              onClick={() => {
                                setEditingConcept(concept);
                                setConceptForm({ name: concept.name, name_en: concept.name_en || '', default_amount: concept.default_amount, includes_iva: concept.includes_iva, iva_rate: concept.iva_rate, type: concept.type });
                                setShowConceptForm(cat.id);
                              }}>
                              <i className="ti-pencil"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-danger" style={{ padding: '2px 6px' }} onClick={() => handleDeleteConcept(cat.id, concept)}>
                              <i className="ti-trash"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}

                  <button className="btn btn-outline-primary btn-sm" onClick={() => { setShowConCatForm(true); setEditingConCat(null); setConCatForm(emptyConcCategory); }}>
                    <i className="ti-plus mr-1"></i> Nueva Categoría de Conceptos
                  </button>
                </div>
              </div>

              {/* Cuentas bancarias */}
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Cuentas Bancarias</h6>
                  <button className="btn btn-primary btn-sm" onClick={() => { setShowAccountForm(true); setEditingAccount(null); setAccountForm(emptyAccount); }}>
                    <i className="ti-plus mr-1"></i> Agregar
                  </button>
                </div>
                <div className="card-body">
                  {showAccountForm && (
                    <div className="border rounded p-3 mb-3 bg-light">
                      <form onSubmit={handleSaveAccount}>
                        <div className="row">
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label className="small">Etiqueta ES *</label>
                              <input type="text" className="form-control form-control-sm" placeholder="Honorarios Notariales"
                                value={accountForm.label} onChange={e => setAccountForm({ ...accountForm, label: e.target.value })} required />
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label className="small">Etiqueta EN</label>
                              <input type="text" className="form-control form-control-sm" placeholder="Notarial Fees"
                                value={accountForm.label_en} onChange={e => setAccountForm({ ...accountForm, label_en: e.target.value })} />
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label className="small">Banco *</label>
                              <input type="text" className="form-control form-control-sm" placeholder="BBVA"
                                value={accountForm.bank} onChange={e => setAccountForm({ ...accountForm, bank: e.target.value })} required />
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label className="small">Beneficiario *</label>
                              <input type="text" className="form-control form-control-sm"
                                value={accountForm.beneficiary} onChange={e => setAccountForm({ ...accountForm, beneficiary: e.target.value })} required />
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label className="small">Número de cuenta *</label>
                              <input type="text" className="form-control form-control-sm"
                                value={accountForm.account_number} onChange={e => setAccountForm({ ...accountForm, account_number: e.target.value })} required />
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label className="small">CLABE</label>
                              <input type="text" className="form-control form-control-sm"
                                value={accountForm.clabe} onChange={e => setAccountForm({ ...accountForm, clabe: e.target.value })} />
                            </div>
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label className="small">SWIFT</label>
                              <input type="text" className="form-control form-control-sm"
                                value={accountForm.swift} onChange={e => setAccountForm({ ...accountForm, swift: e.target.value })} />
                            </div>
                          </div>

                          <div className="col-12 col-md-6">
                            <div className="form-group">
                              <label className="small">Sucursal</label>
                              <input type="text" className="form-control form-control-sm"
                                value={accountForm.branch} onChange={e => setAccountForm({ ...accountForm, branch: e.target.value })} />
                            </div>
                          </div>

                          <div className="col-12">
                            <div className="form-group">
                              <label className="small">Logo del banco</label>
                              <div className="d-flex align-items-center" style={{ gap: 12 }}>
                                {editingAccount?.logo_url && !accountForm.logo && (
                                  <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                    <img
                                      src={editingAccount.logo_url}
                                      alt="Logo actual"
                                      style={{ height: 36, objectFit: 'contain', border: '1px solid #dee2e6', borderRadius: 4, padding: 4, background: '#fff' }}
                                    />
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={async () => {
                                        await api.put(
                                          `/budget-document-types/${selectedType.id}/bank-accounts/${editingAccount.id}`,
                                          { remove_logo: true }
                                        );
                                        fetchAll();
                                        setEditingAccount(prev => ({ ...prev, logo_url: null, logo_path: null }));
                                      }}
                                    >
                                      Quitar logo
                                    </button>
                                  </div>
                                )}
                                <input
                                  type="file"
                                  className="form-control-file"
                                  accept="image/*"
                                  onChange={e => setAccountForm({ ...accountForm, logo: e.target.files[0] || null })}
                                />
                              </div>
                              {accountForm.logo && (
                                <small className="text-muted mt-1 d-block">
                                  Seleccionado: {accountForm.logo.name}
                                </small>
                              )}
                              <small className="text-muted">PNG o JPG, máximo 2MB. Se mostrará en el PDF del presupuesto.</small>
                            </div>
                          </div>

                        </div>
                        <div className="d-flex flex-wrap" style={{ gap: '6px' }}>
                          <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>Guardar</button>
                          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setShowAccountForm(false); setEditingAccount(null); }}>Cancelar</button>
                        </div>
                      </form>
                    </div>
                  )}

                  {selectedType.bank_accounts?.length === 0 && !showAccountForm && (
                    <div className="text-center text-muted py-3 small">Sin cuentas bancarias configuradas</div>
                  )}

                  {selectedType.bank_accounts?.map(account => (
                    <div key={account.id} className="border rounded p-3 mb-2">
                      <div className="d-flex justify-content-between align-items-start">

                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                          {account.logo_url && (
                            <img
                              src={account.logo_url}
                              alt={account.bank}
                              style={{ height: 40, objectFit: 'contain', border: '1px solid #dee2e6', borderRadius: 4, padding: 4, background: '#fff', flexShrink: 0 }}
                            />
                          )}
                          <div>
                            <strong className="small">{account.label}</strong>
                            {account.label_en && <span className="text-muted small ml-2">/ {account.label_en}</span>}
                            <div className="small text-muted mt-1">
                              <strong>{account.bank}</strong> — {account.beneficiary}
                            </div>
                            <div className="small text-muted">
                              Cuenta: {account.account_number}
                              {account.clabe && ` · CLABE: ${account.clabe}`}
                            </div>
                            {account.branch && <div className="small text-muted">Sucursal: {account.branch}</div>}
                            {selectedType.concept_categories?.filter(c => c.budget_bank_account_id === account.id).map(c => (
                              <span key={c.id} className="badge badge-light border mr-1 mt-1" style={{ fontSize: '10px' }}>
                                {c.name}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="d-flex" style={{ gap: '4px' }}>
                          <button className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 6px' }}
                            onClick={() => {
                              setEditingAccount(account);
                              setAccountForm({ label: account.label, label_en: account.label_en || '', bank: account.bank, beneficiary: account.beneficiary, account_number: account.account_number, clabe: account.clabe || '', swift: account.swift || '', branch: account.branch || '' });
                              setShowAccountForm(true);
                            }}>
                            <i className="ti-pencil"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-danger" style={{ padding: '2px 6px' }} onClick={() => handleDeleteAccount(account)}>
                            <i className="ti-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BudgetDocumentTypes;